import {
  VStack,
  HStack,
  Text,
  Box,
} from "@chakra-ui/react";
import SectionHeader from "../../../CustomUi/SectionHeader";
import { Class, Subject, BehavioralSubject } from "../../../../services/Dtos";
import useThemeColors from "../../../../hooks/useThemeColors";
import useIcons from "../../../../hooks/useIcons";
import CustomButton from "../../../CustomUi/CustomButton";
import CustomCheckBox from "../../../CustomUi/CustomCheckBox";
import { useClassService } from "../../../../hooks/Domain/useClassService";
import { useBreakPoint } from "../../../../hooks/useBreakPoint";
import Empty from "../../../CustomUi/Empty";
import { useNavigate } from "react-router-dom";

interface ClassDetailsPanelProps {
  selectedClass: Class;
  setSelectedClass: (classRoom: Class) => void;
  subjects: Subject[];
  behavioralSubjects: BehavioralSubject[];
  selectedClassSubjects: string[];
  setSelectedClassSubjects: (ids: string[]) => void;
  selectedClassBehavioralSubjects: string[];
  setSelectedClassBehavioralSubjects: (ids: string[]) => void;
  handleEditClass: (classRoom: Class) => void;
  inClassLoading: boolean;
  unsavedChanges: boolean;
  setUnsavedChanges: (changed: boolean) => void;
  accentColor: string;
}

export default function ClassDetailsPanel({
  selectedClass,
  subjects,
  behavioralSubjects,
  selectedClassSubjects,
  setSelectedClassSubjects,
  selectedClassBehavioralSubjects,
  setSelectedClassBehavioralSubjects,
  handleEditClass,
  inClassLoading,
  unsavedChanges,
  setUnsavedChanges,
  accentColor,
}: ClassDetailsPanelProps) {
  const colors = useThemeColors();
  const { isMobile } = useBreakPoint();
  const icons = useIcons();
  const levelSubjects = subjects.filter(
    (s) => s.levelId === selectedClass.levelId
  );
  const levelBehavioralSubjects = behavioralSubjects.filter(
    (s) => s.levelId === selectedClass.levelId
  );
  const navigate = useNavigate();
  const { updateClass, isLoading: isUpdatingClass } = useClassService();

  const handleSubjectToggle = (subjectId: string) => {
    const newSelected = selectedClassSubjects.includes(subjectId)
      ? selectedClassSubjects.filter((id) => id !== subjectId)
      : [...selectedClassSubjects, subjectId];
    setSelectedClassSubjects(newSelected);
    setUnsavedChanges(true);
  };

  const handleBehavioralSubjectToggle = (subjectId: string) => {
    const newSelected = selectedClassBehavioralSubjects.includes(subjectId)
      ? selectedClassBehavioralSubjects.filter((id) => id !== subjectId)
      : [...selectedClassBehavioralSubjects, subjectId];
    setSelectedClassBehavioralSubjects(newSelected);
    setUnsavedChanges(true);
  };

  const handleAssignAllSubjects = () => {
    const allSubjectIds = levelSubjects.map((s) => s.id);
    setSelectedClassSubjects(allSubjectIds);
    setUnsavedChanges(true);
  };

  const handleAssignAllBehavioralSubjects = () => {
    const allBehavioralSubjectIds = levelBehavioralSubjects.map((s) => s.id);
    setSelectedClassBehavioralSubjects(allBehavioralSubjectIds);
    setUnsavedChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!selectedClass) return;

    // Create the updated class object only when saving
    const updatedClass = {
      ...selectedClass,
      subjects: selectedClassSubjects
        .map((id) => levelSubjects.find((s) => s.id === id))
        .filter((subject): subject is Subject => subject !== undefined),
      behavioralSubjects: selectedClassBehavioralSubjects
        .map((id) => levelBehavioralSubjects.find((s) => s.id === id))
        .filter(
          (subject): subject is BehavioralSubject => subject !== undefined
        ),
    };

    try {
      const result = await updateClass(updatedClass.id, updatedClass);
      if (result.result.succeeded) {
        setUnsavedChanges(false);
      }
    } catch (error) {}
  };

  return (
    <VStack gap={{ base: 4, md: 8 }} align="stretch">
      <VStack gap={8} align="start">
        {isMobile && (
          <CustomButton
            colorPalette={colors.schoolAccent.primary}
            label="Edit Class Details"
            onClick={() => handleEditClass(selectedClass)}
            w="full"
          />
        )}
        <SectionHeader
          title={`Assign Subjects to ${selectedClass.className}`}
          icon={icons.main.subject}
          mb={4}
        />
      </VStack>

      <Box borderTop="1px" borderColor={colors.border.secondary} pt={4} />

      <Box>
        <HStack
          justify="space-between"
          align="center"
          mb={4}
          flexDirection={{ base: "column", md: "row" }}
          alignItems={{ base: "stretch", md: "center" }}
        >
          <Text fontSize="lg" fontWeight="semibold" color={colors.text.primary}>
            Cognitive Subjects
          </Text>
          <HStack gap={3}>
            <Text fontSize="sm" color={colors.text.secondary}>
              {selectedClassSubjects.length} selected
            </Text>
            <CustomButton
              size="xs"
              colorPalette={accentColor}
              variant="outline"
              label="Assign All"
              onClick={handleAssignAllSubjects}
              disabled={selectedClassSubjects.length === levelSubjects.length}
            />
          </HStack>
        </HStack>

       {levelSubjects.length > 0 ? <VStack align="start" gap={3}>
          {levelSubjects.map((subject) => (
            <CustomCheckBox
              key={subject.id}
              checked={selectedClassSubjects.includes(subject.id)}
              label={subject.subjectName}
              onChange={() => handleSubjectToggle(subject.id)}
            />
          ))}
        </VStack> : <Empty
              message="Add Cognitive Subjects for this level"
              hidePicture
              hasButton
              buttonText="Go to Subject Settings"
              handleClick={() => navigate("/admin/academic/subjects")}
            />}
      </Box>

      <Box borderTop="1px" borderColor={colors.border.secondary} pt={4} />

      <Box>
        <HStack
          justify="space-between"
          align="center"
          mb={4}
          flexDirection={{ base: "column", md: "row" }}
          alignItems={{ base: "stretch", md: "center" }}
        >
          <Text fontSize="lg" fontWeight="semibold" color={colors.text.primary}>
            Behavioral Subjects
          </Text>
          <HStack gap={3}>
            <Text fontSize="sm" color={colors.text.secondary}>
              {selectedClassBehavioralSubjects.length} selected
            </Text>
            <CustomButton
              size="xs"
              colorPalette={accentColor}
              variant="outline"
              label="Assign All"
              onClick={handleAssignAllBehavioralSubjects}
              disabled={
                selectedClassBehavioralSubjects.length ===
                levelBehavioralSubjects.length
              }
            />
          </HStack>
        </HStack>

       {levelBehavioralSubjects.length > 0 ? <VStack align="start" gap={3}>
          {levelBehavioralSubjects.map((subject) => (
            <CustomCheckBox
              key={subject.id}
              checked={selectedClassBehavioralSubjects.includes(subject.id)}
              label={subject.name}
              onChange={() => handleBehavioralSubjectToggle(subject.id)}
            />
          ))}
        </VStack> : <Empty
              message="Add Behavioral Subjects for this level"
              hidePicture
              hasButton
              buttonText="Go to Subject Settings"
              handleClick={() => navigate("/admin/academic/subjects")}
            />}
      </Box>

      <Box borderTop="1px" borderColor={colors.border.secondary} pt={4} />

      <HStack
        flexDirection={{ base: "column", md: "row" }}
        gap={4}
        justify="space-between"
        alignItems={{ base: "stretch", md: "center" }}
      >
        <Text
          fontSize="sm"
          color={colors.text.secondary}
          w={{ base: "100%", md: "auto" }}
        >
          {unsavedChanges ? "You have unsaved changes" : "All changes saved"}
        </Text>
        <CustomButton
          colorPalette={accentColor}
          label="Save Changes"
          icon={icons.utility.check}
          onClick={handleSaveChanges}
          loading={inClassLoading || isUpdatingClass}
          disabled={!unsavedChanges}
          w={{ base: "100%", md: "auto" }}
        />
      </HStack>
    </VStack>
  );
}
